import { apiService } from './apiService';
import { TrainingAssignment, TrainingModule, Exam } from '../types/training';

export interface Certificate {
  id: string;
  practitionerId: string;
  moduleId: string;
  moduleName: string;
  s3Key: string;
  generatedAt: string;
  downloadUrl: string;
  score: number;
}

export interface ExamSubmission {
  answers: Array<{
    questionId: string;
    selectedOptionId: string;
  }>;
}

export interface ExamResult {
  attemptId: string;
  score: number;
  passed: boolean;
  answers: Array<{
    questionId: string;
    selectedOptionId: string;
    correct: boolean;
  }>;
  completedAt: string;
  certificateId?: string;
}

class PractitionerService {
  // Get all assignments for the current practitioner
  async getMyAssignments(): Promise<TrainingAssignment[]> {
    return apiService.get<TrainingAssignment[]>('/assignments/my');
  }

  // Get a specific assignment
  async getAssignment(assignmentId: string): Promise<TrainingAssignment> {
    return apiService.get<TrainingAssignment>(`/assignments/${assignmentId}`);
  }

  // Get training module details
  async getModule(moduleId: string): Promise<TrainingModule> {
    return apiService.get<TrainingModule>(`/modules/${moduleId}`);
  }

  // Get content URL for viewing
  async getContentUrl(fileId: string): Promise<{ url: string }> {
    return apiService.get<{ url: string }>(`/content/${fileId}/url`);
  }

  // Mark content as viewed
  async markContentViewed(assignmentId: string): Promise<void> {
    return apiService.post(`/assignments/${assignmentId}/view-content`);
  }

  // Get exam for a module
  async getExam(examId: string): Promise<Exam> {
    return apiService.get<Exam>(`/exams/${examId}`);
  }

  // Start an exam attempt
  async startExam(assignmentId: string): Promise<{ attemptId: string }> {
    return apiService.post<{ attemptId: string }>(`/assignments/${assignmentId}/start-exam`);
  }

  // Submit exam answers
  async submitExam(attemptId: string, submission: ExamSubmission): Promise<ExamResult> {
    return apiService.post<ExamResult>(`/exam-attempts/${attemptId}/submit`, submission);
  }

  // Get my certificates
  async getMyCertificates(): Promise<Certificate[]> {
    return apiService.get<Certificate[]>('/certificates/my');
  }

  // Get certificate download URL
  async getCertificateUrl(certificateId: string): Promise<{ url: string }> {
    return apiService.get<{ url: string }>(`/certificates/${certificateId}/url`);
  }
}

export const practitionerService = new PractitionerService();
